/**
 * <product-gallery> — thumbnails, hover zoom and the media lightbox.
 *
 * A part carries up to 12 photos and up to 4 videos, sharing one strip in the
 * merchant's order. Everything below keys off one piece of state, the active
 * media index:
 *
 *  - Thumbnails swap the stage. Only the first few render; the overflow tile
 *    opens the lightbox, which is the only route to the rest.
 *  - Hovering a photo floats a magnified panel out to its right. The zoom is
 *    computed against the image's *rendered content box*, not its element box —
 *    object-fit: contain letterboxes a non-square photo inside a square frame,
 *    and measuring the element would put the magnified region out of register.
 *  - Selecting a video hands the stage to a player and hides the zoom controls;
 *    there is nothing to magnify, and the player owns the clicks.
 *  - Clicking a photo opens the lightbox: media at 2/3, title and every
 *    thumbnail at 1/3.
 *
 * Hover zoom is gated on (hover: hover). On a touch screen there is no cursor to
 * track, and the lightbox is the right affordance anyway.
 */
const GALLERY_MIN_ZOOM = 2;
const GALLERY_MAX_ZOOM = 5;

class ProductGallery extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;

    try {
      this.media = JSON.parse(this.querySelector('[data-gallery-media]')?.textContent ?? '[]');
    } catch {
      this.media = [];
    }
    if (!this.media.length) return;

    this.index = 0;
    this.factor = this.clampZoom(Number(this.dataset.zoomFactor) || GALLERY_MIN_ZOOM);

    this.stage = this.querySelector('[data-gallery-stage]');
    this.viewport = this.querySelector('[data-gallery-open]');
    this.main = this.querySelector('[data-gallery-main]');
    this.lens = this.querySelector('[data-gallery-lens]');
    this.panel = this.querySelector('[data-gallery-zoom-panel]');
    this.zoomControls = this.querySelector('[data-gallery-zoom-controls]');
    this.zoomLevel = this.querySelector('[data-gallery-zoom-level]');
    this.videos = [...this.querySelectorAll('[data-gallery-video]')];

    // Sibling of this element, not a child — scope to the section so a second
    // product section on the page couldn't drive this one's lightbox.
    const root = this.closest('.shopify-section') ?? document;
    this.lightbox = root.querySelector('[data-gallery-lightbox]');

    this.wireThumbs();
    this.wireZoom();
    this.wireLightbox();

    this.paintZoomLevel();
  }

  clampZoom(value) {
    return Math.min(GALLERY_MAX_ZOOM, Math.max(GALLERY_MIN_ZOOM, value));
  }

  get current() {
    return this.media[this.index];
  }

  /** The single mutation point: everything else re-renders from here. */
  setIndex(index) {
    if (index < 0 || index >= this.media.length) return;
    this.index = index;

    const item = this.current;
    const isVideo = item.type === 'video';

    // A player left running behind a switched-away stage keeps talking.
    this.pauseAll();

    if (!isVideo) {
      this.main.src = item.main;
      this.main.alt = item.alt;
      // Stale zoom art would otherwise flash on the next hover.
      this.panel.style.backgroundImage = '';
    }

    this.viewport.hidden = isVideo;
    if (this.zoomControls) this.zoomControls.hidden = isVideo;
    if (isVideo) this.hideZoom();

    for (const slot of this.videos) {
      slot.hidden = Number(slot.dataset.galleryVideo) !== index;
    }

    this.paintThumbs();
    this.paintLightbox();
  }

  pauseAll() {
    for (const player of this.querySelectorAll('video')) player.pause();
    for (const player of this.lightbox?.querySelectorAll('video') ?? []) player.pause();
  }

  wireThumbs() {
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.moreTile = this.querySelector('[data-gallery-more]');

    for (const thumb of this.thumbs) {
      thumb.addEventListener('click', () => this.setIndex(Number(thumb.dataset.galleryThumb)));
    }

    // The tile depicts the first item that didn't fit, so open there rather
    // than resetting the viewer to the top.
    this.moreTile?.addEventListener('click', () => this.openLightbox(this.thumbs.length));
    this.viewport?.addEventListener('click', () => this.openLightbox(this.index));
  }

  paintThumbs() {
    for (const thumb of this.thumbs) {
      if (Number(thumb.dataset.galleryThumb) === this.index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    }

    // An item reached through the lightbox has no thumbnail of its own, so the
    // overflow tile stands in for it rather than leaving nothing marked.
    if (this.moreTile) {
      if (this.index >= this.thumbs.length) this.moreTile.setAttribute('aria-current', 'true');
      else this.moreTile.removeAttribute('aria-current');
    }
  }

  /* ---- Hover zoom ---- */

  wireZoom() {
    for (const button of this.querySelectorAll('[data-gallery-zoom]')) {
      button.addEventListener('click', () => {
        const step = button.dataset.galleryZoom === 'in' ? 1 : -1;
        this.factor = this.clampZoom(this.factor + step);
        this.paintZoomLevel();
      });
    }

    if (!window.matchMedia('(hover: hover)').matches) return;

    this.stage.addEventListener('mouseenter', () => {
      if (this.lightbox?.open || this.current.type === 'video') return;
      // Set here rather than on every index change: the zoom source is a large
      // render and there's no reason to fetch it for someone who never hovers.
      this.panel.style.backgroundImage = `url("${this.current.zoom}")`;
      this.lens.hidden = false;
      this.panel.hidden = false;
    });

    this.stage.addEventListener('mouseleave', () => this.hideZoom());

    this.stage.addEventListener('mousemove', (event) => {
      if (this.panel.hidden) return;
      this.trackZoom(event);
    });
  }

  hideZoom() {
    this.lens.hidden = true;
    this.panel.hidden = true;
  }

  paintZoomLevel() {
    if (this.zoomLevel) this.zoomLevel.textContent = `${this.factor}x`;

    for (const button of this.querySelectorAll('[data-gallery-zoom]')) {
      const isIn = button.dataset.galleryZoom === 'in';
      button.disabled = isIn
        ? this.factor >= GALLERY_MAX_ZOOM
        : this.factor <= GALLERY_MIN_ZOOM;
    }
  }

  /**
   * Where the photo actually is inside the <img> box.
   *
   * object-fit: contain centres the image and letterboxes the remainder, and the
   * element also carries padding. Measuring the element box instead of this
   * would offset the magnified region by however much letterboxing there is.
   */
  contentRect() {
    const box = this.main.getBoundingClientRect();
    const style = getComputedStyle(this.main);
    const padLeft = parseFloat(style.paddingLeft);
    const padTop = parseFloat(style.paddingTop);
    const innerW = box.width - padLeft - parseFloat(style.paddingRight);
    const innerH = box.height - padTop - parseFloat(style.paddingBottom);
    const ratio = (this.main.naturalWidth || 1) / (this.main.naturalHeight || 1);

    let width = innerW;
    let height = innerW / ratio;
    if (height > innerH) {
      height = innerH;
      width = innerH * ratio;
    }

    return {
      left: box.left + padLeft + (innerW - width) / 2,
      top: box.top + padTop + (innerH - height) / 2,
      width,
      height,
    };
  }

  trackZoom(event) {
    const image = this.contentRect();
    if (!image.width || !image.height) return;

    // The lens covers exactly the region the panel displays: shrink the panel by
    // the magnification and that's the window onto the photo.
    const lensW = Math.min(this.panel.offsetWidth / this.factor, image.width);
    const lensH = Math.min(this.panel.offsetHeight / this.factor, image.height);

    const clamp = (value, max) => Math.max(0, Math.min(value, max));
    const lensX = clamp(event.clientX - image.left - lensW / 2, image.width - lensW);
    const lensY = clamp(event.clientY - image.top - lensH / 2, image.height - lensH);

    const stage = this.stage.getBoundingClientRect();
    Object.assign(this.lens.style, {
      width: `${lensW}px`,
      height: `${lensH}px`,
      left: `${image.left - stage.left + lensX}px`,
      top: `${image.top - stage.top + lensY}px`,
    });

    Object.assign(this.panel.style, {
      backgroundSize: `${image.width * this.factor}px ${image.height * this.factor}px`,
      backgroundPosition: `${-lensX * this.factor}px ${-lensY * this.factor}px`,
    });
  }

  /* ---- Lightbox ---- */

  wireLightbox() {
    if (!this.lightbox) return;

    this.lightboxImage = this.lightbox.querySelector('[data-lightbox-main]');
    this.lightboxCounter = this.lightbox.querySelector('[data-lightbox-counter]');
    this.lightboxThumbs = this.lightbox.querySelector('[data-lightbox-thumbs]');
    this.lightboxVideos = [...this.lightbox.querySelectorAll('[data-lightbox-video]')];

    // Every item gets a thumbnail here, unlike the capped strip on the page.
    const frag = document.createDocumentFragment();
    this.media.forEach((item, i) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'gallery-lightbox__thumb';
      button.setAttribute(
        'aria-label',
        `${item.type === 'video' ? 'Play video' : 'Show image'} ${i + 1} of ${this.media.length}`
      );

      const img = document.createElement('img');
      img.src = item.thumb;
      img.alt = '';
      img.loading = 'lazy';
      button.appendChild(img);

      if (item.type === 'video') {
        const badge = document.createElement('span');
        badge.className = 'product-gallery__play';
        badge.setAttribute('aria-hidden', 'true');
        button.appendChild(badge);
      }

      button.addEventListener('click', () => this.setIndex(i));
      frag.appendChild(button);
    });
    this.lightboxThumbs.replaceChildren(frag);

    this.lightbox.querySelector('[data-gallery-lightbox-close]')
      ?.addEventListener('click', () => this.lightbox.close());

    // The dialog's own box is the panel, so a click landing on the dialog itself
    // came from the backdrop around it.
    this.lightbox.addEventListener('click', (event) => {
      if (event.target === this.lightbox) this.lightbox.close();
    });

    this.lightbox.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      const step = event.key === 'ArrowRight' ? 1 : -1;
      // Wraps, so holding an arrow key never dead-ends.
      this.setIndex((this.index + step + this.media.length) % this.media.length);
    });

    // Escape and the close button both land here; stop any player still running.
    this.lightbox.addEventListener('close', () => this.pauseAll());
  }

  paintLightbox() {
    if (!this.lightboxImage) return;

    const item = this.current;
    const isVideo = item.type === 'video';

    if (!isVideo) {
      this.lightboxImage.src = item.main;
      this.lightboxImage.alt = item.alt;
    }
    this.lightboxImage.hidden = isVideo;

    for (const slot of this.lightboxVideos) {
      slot.hidden = Number(slot.dataset.lightboxVideo) !== this.index;
    }

    if (this.lightboxCounter) {
      this.lightboxCounter.textContent = `${this.index + 1} of ${this.media.length}`;
    }

    [...this.lightboxThumbs.children].forEach((thumb, i) => {
      if (i === this.index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
  }

  openLightbox(index) {
    if (!this.lightbox) return;
    this.setIndex(index);

    // Hover state would otherwise stay stranded behind the backdrop.
    this.hideZoom();

    this.lightbox.showModal();
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}
